import { useCallback, useRef } from 'react';

export interface UseHorizontalPaneResizeOptions {
  enabled: boolean;
  minWidth: number;
  maxWidth: number;
  getStartWidth: () => number;
  onWidthChange: (width: number) => void;
  /** Panel is on the right; dragging the handle right shrinks the panel. */
  panelOnRight?: boolean;
}

export function useHorizontalPaneResize({
  enabled,
  minWidth,
  maxWidth,
  getStartWidth,
  onWidthChange,
  panelOnRight = true,
}: UseHorizontalPaneResizeOptions): (event: React.MouseEvent) => void {
  const resizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  return useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) return;
      event.preventDefault();

      resizingRef.current = true;
      startXRef.current = event.clientX;
      startWidthRef.current = getStartWidth();

      const previousUserSelect = document.body.style.userSelect;
      const previousCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      const onMove = (ev: MouseEvent) => {
        if (!resizingRef.current) return;
        const delta = ev.clientX - startXRef.current;
        const next = panelOnRight
          ? startWidthRef.current - delta
          : startWidthRef.current + delta;
        onWidthChange(Math.min(maxWidth, Math.max(minWidth, Math.round(next))));
      };

      const onUp = () => {
        resizingRef.current = false;
        document.body.style.userSelect = previousUserSelect;
        document.body.style.cursor = previousCursor;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [enabled, getStartWidth, maxWidth, minWidth, onWidthChange, panelOnRight],
  );
}
